import React, { ChangeEvent } from 'react';
import { Grid, TextField, Typography } from '@mui/material';
import { StripeTaxCountryOption } from '../../../reference-data/stripeSupportedTaxIds';
import { FormControl } from '../../../utils/form.utils';

interface StripeBusinessAddressProps {
    addressLine1FormControl: FormControl<string>;
    addressLine2FormControl: FormControl<string>;
    addressStateFormControl: FormControl<string>;
    addressCityFormControl: FormControl<string>;
    addressPostcodeFormControl: FormControl<string>;
    businessCountryFormControl: FormControl<StripeTaxCountryOption | undefined>;
    readonlyCountryOption: StripeTaxCountryOption;
    removeBillingAddressErrorMessage: () => void;
}

export const StripeBusinessAddress = ({
    addressLine1FormControl,
    addressLine2FormControl,
    addressStateFormControl,
    addressCityFormControl,
    addressPostcodeFormControl,
    businessCountryFormControl,
    readonlyCountryOption,
    removeBillingAddressErrorMessage,
}: StripeBusinessAddressProps) => {
    return (
        <Grid container spacing={2}>
            <Grid size={12}>
                <Typography
                    variant="body2"
                    sx={{
                        color: 'text.secondary',
                    }}
                >
                    We need your business details for invoicing purposes.
                </Typography>
            </Grid>
            <Grid size={12}>
                <TextField
                    id="address-line1-input"
                    fullWidth
                    error={addressLine1FormControl?.showError}
                    helperText={
                        addressLine1FormControl?.showError
                            ? addressLine1FormControl?.validationMessage
                            : undefined
                    }
                    type="text"
                    label="Address line 1"
                    autoComplete="off"
                    value={addressLine1FormControl?.value}
                    onBlur={addressLine1FormControl?.handleBlur}
                    onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                        const val: string = e?.target?.value;
                        addressLine1FormControl?.setValue(val);
                        removeBillingAddressErrorMessage();
                    }}
                />
            </Grid>
            <Grid size={12}>
                <TextField
                    id="address-line2-input"
                    fullWidth
                    error={addressLine2FormControl?.showError}
                    helperText={
                        addressLine2FormControl?.showError
                            ? addressLine2FormControl?.validationMessage
                            : undefined
                    }
                    type="text"
                    label="Address line 2"
                    autoComplete="off"
                    value={addressLine2FormControl?.value}
                    onBlur={addressLine2FormControl?.handleBlur}
                    onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                        const val: string = e?.target?.value;
                        addressLine2FormControl?.setValue(val);
                        removeBillingAddressErrorMessage();
                    }}
                />
            </Grid>
            <Grid
                size={{
                    xs: 12,
                    lg: 6,
                }}
            >
                <TextField
                    id="address-city-input"
                    fullWidth
                    error={addressCityFormControl?.showError}
                    helperText={
                        addressCityFormControl?.showError
                            ? addressCityFormControl?.validationMessage
                            : undefined
                    }
                    type="text"
                    label="City"
                    autoComplete="off"
                    value={addressCityFormControl?.value}
                    onBlur={addressCityFormControl?.handleBlur}
                    onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                        const val: string = e?.target?.value;
                        addressCityFormControl?.setValue(val);
                        removeBillingAddressErrorMessage();
                    }}
                />
            </Grid>
            <Grid
                size={{
                    xs: 12,
                    lg: 6,
                }}
            >
                <TextField
                    id="address-state-input"
                    fullWidth
                    error={addressStateFormControl?.showError}
                    helperText={
                        addressStateFormControl?.showError
                            ? addressStateFormControl?.validationMessage
                            : undefined
                    }
                    type="text"
                    label="State"
                    autoComplete="off"
                    value={addressStateFormControl?.value}
                    onBlur={addressStateFormControl?.handleBlur}
                    onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                        const val: string = e?.target?.value;
                        addressStateFormControl?.setValue(val);
                        removeBillingAddressErrorMessage();
                    }}
                />
            </Grid>
            <Grid
                size={{
                    xs: 12,
                    lg: 6,
                }}
            >
                <TextField
                    id="address-postcode-input"
                    fullWidth
                    error={addressPostcodeFormControl?.showError}
                    helperText={
                        addressPostcodeFormControl?.showError
                            ? addressPostcodeFormControl?.validationMessage
                            : undefined
                    }
                    type="text"
                    label="Postal code"
                    autoComplete="off"
                    value={addressPostcodeFormControl?.value}
                    onBlur={addressPostcodeFormControl?.handleBlur}
                    onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                        const val: string = e?.target?.value;
                        addressPostcodeFormControl?.setValue(val);

                        if (addressPostcodeFormControl.showError) {
                            removeBillingAddressErrorMessage();
                        }
                    }}
                />
            </Grid>
            <Grid
                size={{
                    xs: 12,
                    lg: 6,
                }}
            >
                <TextField
                    id="business-country-readonly-select"
                    fullWidth
                    label="Country"
                    value={readonlyCountryOption.label}
                    onBlur={businessCountryFormControl?.handleBlur}
                    error={businessCountryFormControl?.showError}
                    helperText={
                        businessCountryFormControl?.showError
                            ? businessCountryFormControl?.validationMessage
                            : undefined
                    }
                    disabled
                />
            </Grid>
        </Grid>
    );
};
